import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserPlus, Trash2, RotateCw } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import InviteTeammateDialog from "./InviteTeammateDialog";

type Member = {
  user_id: string;
  display_name: string;
  email: string | null;
  joined_at: string;
  is_owner: boolean;
};

type Invite = {
  id: string;
  email: string;
  status: string;
  created_at: string;
  accepted_at: string | null;
  token: string;
};

export default function TeamTab({ orgId, orgName }: { orgId: string; orgName: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const team = useQuery({
    queryKey: ["partner-team", orgId],
    queryFn: async (): Promise<Member[]> => {
      const { data, error } = await supabase.rpc("list_partner_org_team" as any, { _partner_org_id: orgId });
      if (error) throw error;
      return (data ?? []) as Member[];
    },
  });

  const invites = useQuery({
    queryKey: ["partner-invites", orgId],
    queryFn: async (): Promise<Invite[]> => {
      const { data, error } = await supabase
        .from("partner_org_invites")
        .select("id, email, status, created_at, accepted_at, token")
        .eq("partner_org_id", orgId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Invite[];
    },
  });

  const removeMember = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.rpc("remove_partner_org_member" as any, {
        _partner_org_id: orgId,
        _user_id: userId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Teammate removed");
      qc.invalidateQueries({ queryKey: ["partner-team", orgId] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to remove"),
  });

  const revokeInvite = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("partner_org_invites")
        .update({ status: "revoked" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Invite revoked");
      qc.invalidateQueries({ queryKey: ["partner-invites", orgId] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to revoke"),
  });

  const resendInvite = useMutation({
    mutationFn: async (inv: Invite) => {
      const { error } = await supabase.functions.invoke("send-partner-invite", {
        body: { inviteId: inv.id },
      });
      if (error) throw error;
    },
    onSuccess: () => toast.success("Invite email re-sent"),
    onError: (e: any) => toast.error(e.message ?? "Failed to resend"),
  });

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg">Team members</h3>
            <p className="text-xs text-muted-foreground">Anyone here can manage the org profile, leads, and resources.</p>
          </div>
          <Button onClick={() => setOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" /> Invite teammate
          </Button>
        </div>

        {team.isLoading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="divide-y divide-border">
            {(team.data ?? []).map((m) => (
              <div key={m.user_id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {m.display_name}
                    {m.is_owner && <Badge variant="outline" className="text-[10px]">Owner</Badge>}
                    {m.user_id === user?.id && <Badge variant="secondary" className="text-[10px]">You</Badge>}
                  </div>
                  {m.email && <div className="text-xs text-muted-foreground truncate">{m.email}</div>}
                </div>
                {!m.is_owner && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={removeMember.isPending}
                    onClick={() => {
                      if (confirm(`Remove ${m.display_name} from ${orgName}?`)) removeMember.mutate(m.user_id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h3 className="font-display text-lg mb-1">Pending invites</h3>
        <p className="text-xs text-muted-foreground mb-4">Teammates accept their invite via the email link.</p>

        {invites.isLoading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (invites.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No invites yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {invites.data!.map((i) => (
              <div key={i.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{i.email}</div>
                  <div className="text-xs text-muted-foreground">
                    Sent {new Date(i.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={i.status === "pending" ? "outline" : "secondary"} className="text-[10px] capitalize">
                    {i.status}
                  </Badge>
                  {i.status === "pending" && (
                    <>
                      <Button variant="ghost" size="sm" disabled={resendInvite.isPending} onClick={() => resendInvite.mutate(i)}>
                        <RotateCw className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" disabled={revokeInvite.isPending} onClick={() => revokeInvite.mutate(i.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <InviteTeammateDialog
        open={open}
        onOpenChange={setOpen}
        orgId={orgId}
        orgName={orgName}
        onSent={() => qc.invalidateQueries({ queryKey: ["partner-invites", orgId] })}
      />
    </div>
  );
}